
import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { NAV_ITEMS, CONTACT_INFO, SOCIAL_LINKS, Icons } from '../constants';
import PrivacyCookiesModal from './PrivacyCookiesModal';
import { CONTENT } from '../content';

const Footer: React.FC = () => {
  const [modalOpen, setModalOpen] = useState(false);
  const [modalTab, setModalTab] = useState<'privacy' | 'cookies'>('privacy');
  const location = useLocation();
  const navigate = useNavigate();

  const openModal = (tab: 'privacy' | 'cookies') => {
    setModalTab(tab);
    setModalOpen(true);
  };

  const handleNavClick = (e: React.MouseEvent, path: string) => {
    if (path.startsWith('#')) {
      e.preventDefault();
      if (location.pathname !== '/') {
        navigate('/' + path);
      } else {
        const elementId = path.replace('#', '');
        const element = document.getElementById(elementId);
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  const handleOpenFAQ = (e: React.MouseEvent) => {
    e.preventDefault();
    window.dispatchEvent(new CustomEvent('openFAQModal'));
  };

  // Google Star Icon Component - Classic Google Gold
  const GoogleStars = () => (
    <div className="flex space-x-1 items-center">
      {[1, 2, 3, 4, 5].map((i) => (
        <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill="#FABB05" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" />
        </svg>
      ))}
    </div>
  );

  return (
    <footer className="bg-white pt-24 pb-12">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-16 lg:gap-24">
          <div className="col-span-1 md:col-span-1">
            <div className="flex flex-col leading-none mb-8">
                <span className="text-2xl font-light tracking-[0.2em] text-primary uppercase">LIZA MOERMAN</span>
                <span className="text-[9px] tracking-[0.5em] text-accent uppercase mt-2 font-bold">COACH</span>
            </div>
            <p className="text-sm text-gray-500 leading-relaxed font-light italic mb-8">
              {CONTENT.footer.description}
            </p>
            
            {/* Google Review Section - Prepared for coupling */}
            <a 
              href={CONTENT.footer.reviews.url} 
              target="_blank" 
              rel="noopener noreferrer"
              className="flex flex-col space-y-3 group cursor-pointer"
            >
              <div className="flex items-center space-x-2">
                <svg className="w-4 h-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-1 .67-2.26 1.07-3.71 1.07-2.87 0-5.3-1.94-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.04c-.22-.66-.35-1.36-.35-2.04s.13-1.38.35-2.04V7.12H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.88l3.66-2.84z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.66l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.12l3.66 2.84c.86-2.59 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
                <span className="text-[11px] font-bold tracking-[0.1em] text-gray-800 uppercase">Google Reviews</span>
              </div>
              <div className="flex items-center space-x-3">
                <GoogleStars />
                <span className="text-[10px] font-bold text-gray-400 tracking-wider">{CONTENT.footer.reviews.rating.toFixed(1)}</span>
              </div>
              <p className="text-[10px] text-gray-400 font-medium uppercase tracking-[0.15em] opacity-80 group-hover:opacity-100 transition-opacity">
                {CONTENT.footer.reviews.count} {CONTENT.footer.reviews.label}
              </p>
            </a>
          </div>

          <div>
            <h4 className="text-[10px] uppercase tracking-[0.4em] font-bold mb-8 text-primary opacity-60">{CONTENT.footer.navigationLabel}</h4>
            <ul className="space-y-4">
              {NAV_ITEMS.map(item => (
                <li key={item.path}>
                  {item.path.startsWith('#') ? (
                    <Link 
                      to={location.pathname === '/' ? item.path : '/' + item.path}
                      onClick={(e) => handleNavClick(e, item.path)}
                      className="text-[10px] text-gray-400 hover:text-primary transition-colors font-bold uppercase tracking-[0.2em]"
                    >
                      {item.label}
                    </Link>
                  ) : (
                    <Link 
                      to={'/' + item.path}
                      className="text-[10px] text-gray-400 hover:text-primary transition-colors font-bold uppercase tracking-[0.2em]"
                    >
                      {item.label}
                    </Link>
                  )}
                </li>
              ))}
              <li>
                <button
                  onClick={handleOpenFAQ}
                  className="text-[10px] text-gray-400 hover:text-primary transition-colors font-bold uppercase tracking-[0.2em] text-left cursor-pointer"
                >
                  FAQ/Veelgestelde vragen
                </button>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-[10px] uppercase tracking-[0.4em] font-bold mb-8 text-primary opacity-60">{CONTENT.footer.focusLabel}</h4>
            <ul className="space-y-4">
              <li>
                <Link 
                  to={location.pathname === '/' ? '#stress-check' : '/#stress-check'} 
                  onClick={(e) => handleNavClick(e, '#stress-check')} 
                  className="text-[10px] text-gray-400 hover:text-primary uppercase tracking-[0.2em] font-bold"
                >
                  {CONTENT.footer.focus.stressCheck}
                </Link>
              </li>
              <li>
                <Link 
                  to={location.pathname === '/' ? '#werkwijze' : '/#werkwijze'} 
                  onClick={(e) => handleNavClick(e, '#werkwijze')} 
                  className="text-[10px] text-gray-400 hover:text-primary uppercase tracking-[0.2em] font-bold"
                >
                  {CONTENT.footer.focus.stressBurnout}
                </Link>
              </li>
              <li>
                <Link 
                  to={location.pathname === '/' ? '#werkwijze' : '/#werkwijze'} 
                  onClick={(e) => handleNavClick(e, '#werkwijze')} 
                  className="text-[10px] text-gray-400 hover:text-primary uppercase tracking-[0.2em] font-bold"
                >
                  {CONTENT.footer.focus.stressRecovery}
                </Link>
              </li>
            </ul>
          </div>
          <div>
            <h4 className="text-[10px] uppercase tracking-[0.4em] font-bold mb-8 text-primary opacity-60">{CONTENT.footer.locationLabel}</h4>
            <div className="text-xs text-gray-500 space-y-6 font-light">
              <p className="flex flex-col">
                <span className="font-bold text-primary uppercase text-[9px] tracking-[0.3em] mb-2 opacity-80">{CONTENT.footer.addressLabel}</span>
                {CONTACT_INFO.address}<br />{CONTACT_INFO.zipCity}
              </p>
              <p className="flex flex-col">
                <span className="font-bold text-primary uppercase text-[9px] tracking-[0.3em] mb-2 opacity-80">{CONTENT.footer.onlineLabel}</span>
                <a href={`mailto:${CONTACT_INFO.email}`} className="hover:text-primary transition-colors">{CONTACT_INFO.email}</a><br />
                <a href={`tel:${CONTACT_INFO.phone.replace(/\s/g, '')}`} className="hover:text-primary transition-colors">{CONTACT_INFO.phone}</a>
              </p>
              {/* Social Media Icons Section */}
              <div className="flex flex-col pt-2">
                <span className="font-bold text-primary uppercase text-[9px] tracking-[0.3em] mb-4 opacity-80">{CONTENT.footer.volgLabel}</span>
                <div className="flex space-x-5 items-center">
                  <a 
                    href={SOCIAL_LINKS.instagram} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-primary transition-all duration-300 transform hover:scale-110"
                    aria-label="Volg Liza Moerman op Instagram"
                  >
                    <Icons.Instagram />
                  </a>
                  <a 
                    href={SOCIAL_LINKS.linkedin} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-gray-400 hover:text-primary transition-all duration-300 transform hover:scale-110"
                    aria-label="Connect met Liza Moerman op LinkedIn"
                  >
                    <Icons.Linkedin />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="mt-24 pt-10 border-t-2 border-accent-light/40 flex flex-col md:flex-row justify-between items-center space-y-6 md:space-y-0 text-[9px] text-gray-400 uppercase tracking-[0.3em] font-bold">
          <p>&copy; {new Date().getFullYear()} {CONTENT.footer.copyright} <Link to="/admin" className="text-gray-200/50 hover:text-accent font-sans ml-1 text-xs select-none">.</Link></p>
          <div className="flex space-x-10">
            <button 
              onClick={handleOpenFAQ}
              className="hover:text-primary transition-colors uppercase tracking-[0.3em] cursor-pointer"
            >
              FAQ
            </button>
            <button 
              onClick={() => openModal('privacy')}
              className="hover:text-primary transition-colors uppercase tracking-[0.3em] cursor-pointer"
            >
              {CONTENT.footer.privacy}
            </button>
            <button 
              onClick={() => openModal('cookies')}
              className="hover:text-primary transition-colors uppercase tracking-[0.3em] cursor-pointer"
            >
              {CONTENT.footer.cookies}
            </button>
          </div>
        </div>
      </div>

      <PrivacyCookiesModal 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)} 
        initialTab={modalTab} 
      />
    </footer>
  );
};

export default Footer;
